import { ApiError, type ApiClient, type ChatMessageView, type ChatResponse, type CreateCharacterRequest, type MeResponse, type PlayerSnapshot, type RequestOf, type RouteName, TERMS_VERSION } from '@wyn/contracts';
import { keepPreviousData, type QueryClient, queryOptions, useMutation, useQueryClient } from '@tanstack/react-query';
import { newActionId } from './ids.ts';
import { realtime } from './realtime.ts';
import { rt } from './runtime.ts';

/** Fallback polling interval while there is no live socket. */
export const POLL_MS = 30_000;

/** Poll only while the socket is down; when it is live, pushes say what to refetch. Read after every fetch. */
export const pollInterval = () => (realtime().status === 'live' ? false : POLL_MS);

const retry = (n: number, err: Error) => !(err instanceof ApiError && err.status < 500) && n < 3;

export const meQuery = () => queryOptions({ queryKey: ['me'], queryFn: () => rt().api.me(), refetchInterval: pollInterval, retry });
export const eventsQuery = () => queryOptions({ queryKey: ['events'], queryFn: () => rt().api.events({}), refetchInterval: pollInterval, retry });
/** The Deeds page: deeds done, visible ones still to do, and a count of the hidden ones. */
export const deedsQuery = () => queryOptions({ queryKey: ['deeds'], queryFn: () => rt().api.deeds(), retry });
/** Folk about town, newest arrivals first; `name` searches a town too busy to list everyone. */
export const townQuery = (name = '') =>
  queryOptions({ queryKey: ['town', name], queryFn: () => rt().api.town(name ? { name } : {}), refetchInterval: pollInterval, retry, placeholderData: keepPreviousData });

/**
 * A refusal for a ban, a mute or a forced rename means the player's snapshot is out of date (a steward
 * acted since it was fetched): refetch `me`, and the shell shows the ban, the chat box the silence, or
 * the page asking for a new name.
 */
export function refetchOnSanction(qc: QueryClient, err: unknown) {
  if (err instanceof ApiError && (err.code === 'BANNED' || err.code === 'MUTED' || err.code === 'RENAME_REQUIRED')) void qc.invalidateQueries({ queryKey: ['me'] });
}

/** Cached queries an action may need refetched. */
export type QueryKeyName = 'me' | 'events' | 'town' | 'bank' | 'book' | 'orders' | 'offers' | 'chat';

/** Routes that take an actionId and answer with the new player snapshot. */
type ActionRoute = 'setChat' | 'setAnalytics' | 'setTitle' | 'dismissFirstSteps' | 'train' | 'travel' | 'crime' | 'bail' | 'useItem' | 'buy' | 'loadout' | 'changeCalling' | 'craft' | 'deposit' | 'withdraw' | 'lock' | 'placeOrder' | 'cancelOrder' | 'proposeTrade' | 'acceptTrade';
type ActionResponse<K extends ActionRoute> = Awaited<ReturnType<ApiClient[K]>>;

/**
 * Every action gets its actionId when the player acts, so retries of the same click are idempotent.
 * The snapshot in the reply replaces the cached player; actions that touch the pack or the log refetch them.
 */
export function useAction<K extends ActionRoute>(route: K, opts: { refetch?: QueryKeyName[] } = {}) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (req: Omit<RequestOf<K>, 'actionId'>) =>
      (rt().api[route] as (b: unknown) => Promise<ActionResponse<K>>)({ ...req, actionId: newActionId() }),
    onSuccess: (res) => {
      const player = (res as { player: PlayerSnapshot }).player;
      qc.setQueryData<MeResponse>(['me'], (old) => (old ? { ...old, player } : old));
      for (const key of new Set(['events' as const, ...(opts.refetch ?? [])])) void qc.invalidateQueries({ queryKey: [key] });
    },
  });
}

export const useTrain = () => useAction('train');

/**
 * Agree to the current rules and terms after they change; the shell lets the player back in at once.
 * A player from before birth months were asked gives theirs too, so `me` is fetched again for it.
 */
export function useAcceptTerms() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (born?: string) => rt().api.acceptTerms({ termsVersion: TERMS_VERSION, ...(born ? { born } : {}) }),
    onSuccess: ({ termsVersion }, born) => {
      qc.setQueryData<MeResponse>(['me'], (old) => (old ? { ...old, termsVersion } : old));
      if (born) void qc.invalidateQueries({ queryKey: ['me'] });
    },
  });
}

/** Choose a new name after a steward has taken the old one away; the shell lets the player back in at once. */
export function useChooseName() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (name: string) => rt().api.chooseName({ name, actionId: newActionId() }),
    onSuccess: ({ player }) => {
      qc.setQueryData<MeResponse>(['me'], (old) => (old ? { ...old, player } : old));
      void qc.invalidateQueries({ queryKey: ['events'] });
    },
  });
}

export function useCreateCharacter() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (req: Omit<CreateCharacterRequest, 'actionId'>) => rt().api.createCharacter({ ...req, actionId: newActionId() }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['me'] }),
  });
}

/** Local docker compose only: skip the server clock ahead, then refetch everything. */
export function useDevClock() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (advanceMs: number) => rt().api.devClock({ advanceMs }),
    onSuccess: () => qc.invalidateQueries(),
  });
}

/** Local docker compose only: grow the caller past the Crown's protection, to try fights. */
export function useDevGrant() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (req: RequestOf<'devGrant'>) => rt().api.devGrant(req),
    onSuccess: () => qc.invalidateQueries(),
  });
}

export type { RouteName };

export const fightCurrentQuery = () => queryOptions({ queryKey: ['fight'], queryFn: () => rt().api.fightCurrent(), retry, staleTime: Infinity });

/**
 * Fight calls. Turns replace the cached fight; a reply that carries the player replaces the cached
 * one, and settling refetches the pack (arrows), the Chronicle and the town.
 */
export function useFight() {
  const qc = useQueryClient();
  const setPlayer = (player: PlayerSnapshot | null) => {
    if (player) qc.setQueryData<MeResponse>(['me'], (old) => (old ? { ...old, player } : old));
  };
  const settled = () => {
    for (const key of ['me', 'events', 'town']) void qc.invalidateQueries({ queryKey: [key] });
  };
  const attack = useMutation({
    mutationFn: (target: string) => rt().api.fightAttack({ target, actionId: newActionId() }),
    onSuccess: (r) => {
      setPlayer(r.player);
      qc.setQueryData(['fight'], { fight: r.fight });
    },
  });
  const turn = useMutation({
    mutationFn: (req: Omit<RequestOf<'fightTurn'>, 'actionId'>) => rt().api.fightTurn({ ...req, actionId: newActionId() }),
    onSuccess: (r) => {
      setPlayer(r.player);
      qc.setQueryData(['fight'], { fight: r.settlement ? null : r.fight });
      if (r.settlement) settled();
    },
  });
  const finish = useMutation({
    mutationFn: (req: Omit<RequestOf<'fightFinish'>, 'actionId'>) => rt().api.fightFinish({ ...req, actionId: newActionId() }),
    onSuccess: (r) => {
      setPlayer(r.player);
      qc.setQueryData(['fight'], { fight: null });
      settled();
    },
  });
  const withdraw = useMutation({
    mutationFn: () => rt().api.fightWithdraw({ actionId: newActionId() }),
    onSuccess: (r) => {
      setPlayer(r.player);
      qc.setQueryData(['fight'], { fight: null });
      settled();
    },
  });
  return { attack, turn, finish, withdraw };
}

export const minigameSessionQuery = () =>
  queryOptions({ queryKey: ['minigame-session'], queryFn: () => rt().api.minigameSession(), retry, staleTime: Infinity });

/**
 * Minigame calls. Each reply that carries the player replaces the cached one; settling refetches the
 * pack and skills (`me`) and the Chronicle.
 */
export function useMinigame() {
  const qc = useQueryClient();
  const setPlayer = (player: PlayerSnapshot | null) => {
    if (player) qc.setQueryData<MeResponse>(['me'], (old) => (old ? { ...old, player } : old));
  };
  const settled = () => {
    void qc.invalidateQueries({ queryKey: ['me'] });
    void qc.invalidateQueries({ queryKey: ['events'] });
    qc.setQueryData(['minigame-session'], { session: null });
  };
  const start = useMutation({
    mutationFn: (req: Omit<RequestOf<'minigameStart'>, 'actionId'>) => rt().api.minigameStart({ ...req, actionId: newActionId() }),
    onSuccess: (r) => {
      setPlayer(r.player);
      qc.setQueryData(['minigame-session'], { session: r.session });
    },
  });
  const move = useMutation({
    mutationFn: (req: Omit<RequestOf<'minigameMove'>, 'actionId'>) => rt().api.minigameMove({ ...req, actionId: newActionId() }),
    onSuccess: (r) => {
      setPlayer(r.player);
      if (r.settlement) settled();
    },
  });
  const finish = useMutation({
    mutationFn: (req: Omit<RequestOf<'minigameFinish'>, 'actionId'>) => rt().api.minigameFinish({ ...req, actionId: newActionId() }),
    onSuccess: (r) => {
      setPlayer(r.player);
      settled();
    },
  });
  const abandon = useMutation({
    mutationFn: () => rt().api.minigameAbandon({ actionId: newActionId() }),
    onSuccess: (r) => {
      setPlayer(r.player);
      settled();
    },
  });
  return { start, move, finish, abandon };
}

export const bankQuery = () => queryOptions({ queryKey: ['bank'], queryFn: () => rt().api.bank(), retry });
export const ordersQuery = () => queryOptions({ queryKey: ['orders'], queryFn: () => rt().api.orders(), refetchInterval: pollInterval, retry });
export const bookQuery = (itemKey: string, quality: RequestOf<'book'>['quality']) =>
  queryOptions({ queryKey: ['book', itemKey, quality], queryFn: () => rt().api.book({ itemKey, quality }), refetchInterval: pollInterval, retry });
export const offersQuery = () => queryOptions({ queryKey: ['offers'], queryFn: () => rt().api.offers(), refetchInterval: pollInterval, retry });

/** Turning down (or withdrawing) an offer answers with no player, so it is not a `useAction`. */
export function useDeclineTrade() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (offerId: string) => rt().api.declineTrade({ offerId, actionId: newActionId() }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['offers'] });
      void qc.invalidateQueries({ queryKey: ['events'] });
    },
  });
}

export const boardsQuery = (board: RequestOf<'boards'>['board']) =>
  queryOptions({ queryKey: ['boards', board], queryFn: () => rt().api.boards({ board }), refetchInterval: pollInterval, retry, placeholderData: keepPreviousData });
export const chatQuery = (channel: string) =>
  queryOptions({ queryKey: ['chat', channel], queryFn: () => rt().api.chat({ channel }), refetchInterval: pollInterval, retry });

/**
 * Adds a line to a channel's cached history once, wherever it came from (the reply or the socket).
 * A line a steward removed comes again, and replaces the one already shown.
 */
export function addChatLine(qc: QueryClient, message: ChatMessageView) {
  // Pushed lines from someone the player has blocked are dropped here; the server already leaves them out of `chat`.
  if (qc.getQueryData<MeResponse>(['me'])?.blocks.some((b) => b.id === message.playerId)) return;
  qc.setQueryData<ChatResponse>(['chat', message.channel], (old) => {
    if (!old) return old;
    if (!old.messages.some((m) => m.id === message.id)) return { ...old, messages: [...old.messages, message].slice(-200) };
    return message.removed ? { ...old, messages: old.messages.map((m) => (m.id === message.id ? message : m)) } : old;
  });
}

export function useSay() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (req: { channel: string; text: string }) => rt().api.say({ ...req, actionId: newActionId() }),
    onSuccess: (r) => addChatLine(qc, r.message),
  });
}

/** Turn chat on or off. The Talk page empties or fills again; the server stops or starts pushing lines at once. */
export const useSetChat = () => useAction('setChat', { refetch: ['chat'] });
export const useSetAnalytics = () => useAction('setAnalytics');

/**
 * Block a player by name, or unblock one by id. The reply is the whole list; the cached `me` takes it,
 * and talk, Folk and offers are refetched so the blocked player's lines, presence and offers go (or come back).
 */
function useBlocks<K extends 'block' | 'unblock'>(route: K) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (req: Omit<RequestOf<K>, 'actionId'>) => (rt().api[route] as (b: unknown) => ReturnType<ApiClient[K]>)({ ...req, actionId: newActionId() }),
    onSuccess: ({ blocks }) => {
      qc.setQueryData<MeResponse>(['me'], (old) => (old ? { ...old, blocks } : old));
      for (const key of ['chat', 'town', 'offers']) void qc.invalidateQueries({ queryKey: [key] });
    },
  });
}
export const useBlock = () => useBlocks('block');
export const useUnblock = () => useBlocks('unblock');

/** Report a line or a player to the stewards. The reply says only that it arrived. */
export function useReport() {
  return useMutation({ mutationFn: (req: Omit<RequestOf<'report'>, 'actionId'>) => rt().api.report({ ...req, actionId: newActionId() }) });
}

// ---- The stewards' view ----

export const adminReportsQuery = () => queryOptions({ queryKey: ['admin', 'reports'], queryFn: () => rt().api.adminReports(), retry });
export const adminPlayerQuery = (q: string) => queryOptions({ queryKey: ['admin', 'player', q], queryFn: () => rt().api.adminPlayer({ q }), retry, enabled: q.length > 0, placeholderData: (prev) => prev });
export const adminRealmQuery = () => queryOptions({ queryKey: ['admin', 'realm'], queryFn: () => rt().api.adminRealm(), retry });
export const adminFlagsQuery = () => queryOptions({ queryKey: ['admin', 'flags'], queryFn: () => rt().api.adminFlags(), retry });
export const adminBetweenQuery = (a: string, b: string, enabled: boolean) => queryOptions({ queryKey: ['admin', 'between', a, b], queryFn: () => rt().api.adminBetween({ a, b }), retry, enabled });

type AdminAction = 'adminRemoveLine' | 'adminSanction' | 'adminLift' | 'adminRename' | 'adminResolve';

/** A steward's action. Everything the stewards' view shows is refetched after it. */
export function useAdminAction<K extends AdminAction>(route: K) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (req: RequestOf<K>) => (rt().api[route] as (b: RequestOf<K>) => Promise<unknown>)(req),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin'] }),
  });
}
